import { useEffect } from "react";
import { Redirect, useHistory } from "react-router-dom";
import type { RouteComponentProps } from "react-router-dom";
import { makeAutoObservable } from "mobx";
import { Observer, observer, useLocalObservable } from "mobx-react-lite";
import { getUnixTime } from "date-fns";
import { usePresenceChannel } from "hooks/usePresenceChannel";
import localStorage from "store2";
import { useQueryParam, StringParam } from "use-query-params";
import { useTime } from "react-timer-hook";
import { collection, query, onSnapshot } from "firebase/firestore";

import { Link, LinearProgress, Paper, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { keyframes } from "tss-react";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import TimerIcon from "@mui/icons-material/Timer";
import ErrorTwoToneIcon from "@mui/icons-material/ErrorTwoTone";
import { Transition } from "react-transition-group";

import { Page, useHub } from "components";
import defaultGroups from "../../groups";
import { useFirestore } from "hooks/useFirebase";
import { useStoreId } from "hooks/useStoreId";

const blinkAnimation = keyframes`
	0%, 50%, 100% { opacity: 1; }
	25%, 75% { opacity: 0.5; }
`;

interface StyleParams {
	group?: { color?: string };
}

const useStyles = makeStyles<StyleParams>()((theme: any, { group }) => ({
	root: {
		height: "100vh",
		display: "flex",
		flexDirection: "column",
		paddingBottom: 0,
		backgroundColor: "#202629",
		overflow: "hidden",
	},
	railWrap: {
		display: "flex",
		flex: 1,
		overflow: "hidden",
	},
	headerWrap: {},
	header: {
		padding: theme.spacing(1.5, 4),
		borderBottom: "2px solid #393e44",
		backgroundColor: "#283840",
	},
	headerMessage: {
		color: "#fff",
		textAlign: "center"
	},
	waitTime: {
		margin: theme.spacing(0, 1),
		fontSize: "1.1em",
	},
	groupLegend: {
		display: "flex",
		justifyContent: "center",
		background: "#30313c",
	},
	groupBtn: {
		"& button": {
			width: "100%",
		},
		"& h4": {
			backgroundColor: group?.color,
			padding: theme.spacing(1, 3.5),
			display: "block",
			color: "white",
			textAlign: "center",
			textShadow: "1px 1px 1px rgba(0, 0, 0, 0.3)",
		},
	},
	slotColor: {
		backgroundColor: group?.color,
	},
	rail: {
		display: "flex",
	},
	groupType: {
		width: "50%",
		padding: theme.spacing(2.5),
	},
	ready: {
		"& h3": {
			color: "#95ff2a",
		},
	},
	inProgress: {
		marginRight: "5px",
		borderRight: "2px solid #393e44",
		"& h3": {
			color: "#daf1ff;",
		},
		"@media (orientation: landscape)": {
			width: "75%",
		},
	},
	nothingLabel: {
		color: "#fff"
	},
	groupSlots: {
		display: "flex",
		flexFlow: "wrap",
		padding: theme.spacing(2, 0),
	},
	groupInProgress: {},
	groupReady: {},
	groupHeader: {
		width: "100%",
		marginBottom: theme.spacing(1),
	},
	groupTitle: {
		display: "flex",
		fontSize: "28px",
	},
	groupTitleWrap: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
	},
	groupIcon: {
		marginRight: theme.spacing(0.5),
	},
	// Slot
	slot: {
		padding: "0.25em",
		textAlign: "center",
		minWidth: "10%",
	},
	inProgressSlot: {},
	readySlot: {
		minWidth: "15%",
	},
	slotBtn: {
		padding: theme.spacing(1.8),
		width: "100%",
		display: "flex",
		flexDirection: "column",
	},
	slotGroup: {
		marginBottom: theme.spacing(0.3),
		color: "white",
		fontWeight: 500,
		textShadow: "1px 1px 1px rgba(0, 0, 0, 0.3)",
		lineHeight: 1,
		width: "100%",
	},
	label: {
		fontSize: "3.5em",
		textAlign: "center",
		fontWeight: 500,
		color: "white",
		lineHeight: 1,
		textShadow: "1px 1px 1px rgba(0, 0, 0, 0.3)",
	},
	blink: {
		animation: `${blinkAnimation} 1s both infinite`,
	},
	connectError: {
		color: "red",
		padding: theme.spacing(4),
	},
	slotPending: {},
	slotError: {},
}));

interface RailProps extends RouteComponentProps<any> {
	initialState?: any;
}

export const Rail = (props: RailProps) => {
	const { initialState = {} } = props;
	const { classes: css } = useStyles({ group: undefined });
	const hub = useHub();
	const history = useHistory();

	const groups: any = useLocalObservable(() => ({}));

	const { storeId } = useStoreId();
	const state: any = useLocalObservable(() => ({
		error: null,

		storeId: storeId,
		store: {},

		selectedGroup: null,
		expiryQueue: [],
		waitTime: "",
		changes: 0,
		update() {
			state.changes += 1;
		},

		slots: [],

		...initialState,
	}));

	useEffect(() => {
		async function getStore() {
			if (!state.storeId) {
				return;
			}
			try {
				const response = await hub.getStore();
				const { data: store } = response;
				state.store = store;
				state.waitTime = `${store.waitTime} mins`;

				if (store.groups) {
					const storeGroups = buildGroupsStore(store.groups);
					for (const [key, group] of Object.entries(storeGroups)) {
						groups[key] = group;
					}
				} else {
					for (const [key, group] of Object.entries(defaultGroups)) {
						groups[key] = group;
					}
				}
			} catch (err) {
				const { response } = err;
				if (!response) {
					state.error = true;
				}
				if (response && response.status === 404) {
					history.replace("/");
				}
			}
		}
		getStore();
	}, [
		history,
		hub,
		groups,
		state,
		state.error,
		state.store,
		state.storeId,
		state.waitTime,
	]);

	// handle setting store id via query string
	const [storeParam] = useQueryParam("store", StringParam);
	if (storeParam) {
		localStorage.set("storeId", storeParam, true);
		// strip the query string
		window.location.href = window.location.pathname;
	}

	if (!state.storeId) {
		return <Redirect to="/set-store" />;
	}

	return (
		<Page className={css.root} title="Queue Display">
			<Observer>
				{() => (
					<>
						{state.error && (
							<Typography variant="h1" className={css.connectError}>
								Error getting store information. Refresh or contact support
							</Typography>
						)}
						{!state.error && (
							<>
								{state.storeId && state.store.remote && (
									<CommandListeners state={state} groups={groups} />
								)}

								{state.store?.headerMessage && (
									<div className={css.header}>
										<Typography variant="h2" className={css.headerMessage} data-testid="headerMessage">
											{state.store.headerMessage}
										</Typography>
									</div>
								)}

								{(!state.storeId || !state.store.remote) && (
									<div className={css.groupLegend}>
										{Object.entries(groups).map(([key, group]) => (
											<GroupLegend key={key} group={group} state={state} />
										))}
									</div>
								)}

								<div className={css.railWrap}>
									<Preparing state={state} groups={groups} />
									<Ready state={state} groups={groups} />
								</div>
							</>
						)}
					</>
				)}
			</Observer>
		</Page>
	);
};

const CommandListeners = (props: any) => {
	const { state } = props;
	const { storeId } = state;

	usePresenceChannel(`presence-${storeId}`);
	const { db } = useFirestore();

	useEffect(() => {
		if (!db) {
			return;
		}

		const handleState = (data: any) => {
			const { waitTime } = data;
			state.waitTime = waitTime
				? `${waitTime} mins`
				: `${state.store.waitTime} mins`;
		};

		const q = query(collection(db, storeId));
		onSnapshot(q, (querySnapshot) => {
			const slots: UISlot[] = [];
			querySnapshot.forEach((doc) => {
				if (doc.id === "_state") {
					handleState(doc.data());
					return;
				}
				const data = doc.data();
				const uiSlot = new UISlot({
					...data,
					id: doc.id,
					ready: data.ready ? 1 : 0,
					cleared: 0,
				});
				slots.push(uiSlot);
			});
			state.slots = [...slots];
		});
		// eslint-disable-next-line
	}, [db, storeId, state]);

	return null;
};

const GroupLegend = observer((props: any) => {
	const { group, state } = props;
	const { classes: css, cx } = useStyles({ group });

	const onClickHeader = () => {
		state.selectedGroup = group;
	};

	return (
		<Observer>
			{() => (
				<div className={cx(css.groupBtn)}>
					<Link component="button" underline="none" onClick={onClickHeader}>
						<Typography variant="h4">{group.name}</Typography>
					</Link>
				</div>
			)}
		</Observer>
	);
});

const Ready = observer((props: any) => {
	const { state, groups } = props;
	const { classes: css, cx } = useStyles({ group: undefined });

	const slots = state.slots.filter((o: UISlot) => o.ready);
	if (slots.length > 0) {
		slots.sort((a: UISlot, b: UISlot) =>
			a.slot.readyDate && b.slot.readyDate
				? b.slot.readyDate - a.slot.readyDate
				: 1,
		);
	}

	return (
		<div className={cx(css.groupType, css.ready)}>
			<Typography variant="h3" className={css.groupTitle}>
				<DoneOutlineIcon className={css.groupIcon} /> Ready
			</Typography>
			<div className={cx(css.groupSlots, css.groupReady)}>
				{slots && slots.length > 0 ? (
					slots.map((ro: any) => (
						<Slot
							key={ro.slot.id || ro.slot.created}
							slot={ro}
							groups={groups}
						/>
					))
				) : (
					<Typography variant="h4" className={css.nothingLabel}>Nothing ready</Typography>
				)}
			</div>
		</div>
	);
});

// Group slots according to group slot
const groupSlots = (slots: UISlot[]): UISlot[] => {
	const s = [...slots];
	s.sort((a, b) => {
		if (a.slot.group !== b.slot.group) {
			return 1;
		}
		return 0;
	});
	return s;
};

const Preparing = observer((props: any) => {
	const { state, groups } = props;
	const { classes: css, cx } = useStyles({ group: undefined });

	const slots = groupSlots(state.slots.filter((o: UISlot) => o.preparing));

	return (
		<div className={cx(css.groupType, css.inProgress)}>
			<span className={css.groupTitleWrap}>
				<Typography variant="h3" className={css.groupTitle}>
					<TimerIcon className={css.groupIcon} />
					Preparing
				</Typography>
				<Typography variant="h3" data-testid="waitTime">
					(Around {state.waitTime} wait)
				</Typography>
			</span>
			<div className={cx(css.groupSlots, css.groupInProgress)}>
				{slots && slots.length > 0 ? (
					slots.map((po: UISlot) => (
						<Slot
							key={po.slot.id || po.slot.created}
							slot={po}
							groups={groups}
						/>
					))
				) : (
					<Typography variant="h4" className={css.nothingLabel}>Nothing in progress</Typography>
				)}
			</div>
		</div>
	);
});

export const Slot = observer((props: any) => {
	const { slot: uiSlot, groups, className: _className, ...rest } = props;

	const group = uiSlot ? groups[uiSlot.slot.group] : undefined;
	const { classes: css, cx } = useStyles({ group });
	useTime();

	if (!uiSlot || uiSlot.isHidden) {
		return null;
	}

	const { slot, pending, error } = uiSlot;
	const pfName = group ? group.name.toUpperCase() : null;
	const slotType = slot.ready === 1 ? "ready" : "inProgress";

	// styles
	const slotStyles = {
		[css.slot]: true,
		[css[`${slotType}Slot`]]: true,
	};
	const slotColorStyles = {
		[css.slotColor]: true,
	};

	// transitions
	const transitionStyles: any = {
		entering: css.blink,
		entered: css.blink,
	};
	let transitionIn = false;
	const now = getUnixTime(new Date());
	if (uiSlot.ready && uiSlot.slot.readyDate >= now - 3) {
		transitionIn = true;
	}

	return (
		<Transition in={transitionIn} appear={true} timeout={0}>
			{(transitionState) => {
				if (transitionStyles) {
					slotStyles[transitionStyles[transitionState]] = true;
				}
				return (
					<div className={cx(slotStyles)}>
						<Paper {...rest} className={cx(slotColorStyles, css.slotBtn)} elevation={0}>
							{pending && (
								<LinearProgress
									variant="indeterminate"
									className={css.slotPending}
								/>
							)}
							<Typography variant="subtitle1" className={css.slotGroup}>
								{pfName}
							</Typography>
							<Typography
								className={css.label}
								data-testid={`label-${slotType}`}
							>
								{slot.label}
								{error && <ErrorTwoToneIcon className={css.slotError} />}
							</Typography>
						</Paper>
					</div>
				);
			}}
		</Transition>
	);
});

const buildGroupsStore = (data: Group[]) => {
	const store: { [x: string]: Group } = {};
	data.forEach((group) => {
		store[group.id] = group;
	});
	return store;
};

export class UISlot {
	slot: any;
	pending: boolean;
	error: boolean;
	isHidden: boolean;

	constructor(slot: any, pending: boolean = false, error: boolean = false) {
		makeAutoObservable(this);
		this.slot = slot;
		this.pending = pending;
		this.error = error;
		this.isHidden = false;
	}

	get ready() {
		if (Object.hasOwn(this.slot, "state")) {
			return this.slot.state === 2;
		}
		return this.slot.ready === 1 && this.slot.cleared === 0;
	}

	get preparing() {
		if (Object.hasOwn(this.slot, "state")) {
			return this.slot.state === 1;
		}
		return this.slot.ready === 0 && this.slot.cleared === 0;
	}
}

export interface Group {
	id: string;
	name: string;
	color: string;
	sequenceId: boolean;
	[x: string]: any;
}

export default Rail;
